// Third party libraries
import _ from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { Container } from 'semantic-ui-react';

// Redux
import { closeLoader, openLoader } from '../../../redux-store/loader';

// Models
import GroupModel, { GroupInterface } from '../../../models/group';
import ProgressModel from '../../../models/progress';

// Components
import BottomNav from '../../components/bottomNav/BottomNav';
import CustomList, { ItemActionInterface, ItemInterface } from '../../components/list';
import PanelNav from '../../components/nav/PanelNav';
import Submenu from '../../components/nav/Submenu';
import ImageViewer from '../../components/imageViewer';
import TutorialStarter from '../../hooks/tutorials/TutorialStarter';

// Styled components
import { Wrapper } from '../../styled/wrappers';

// Locales
import I18n from '../../../i18n';
import GenericModal, { ClosedModal, GenericModalProps } from '../../components/genericModal';
import OButton from '../../styled/button';

// Interfaces
interface MapDispatchProps {
    openLoader: any;
    closeLoader: any;
};

interface GroupsProps extends MapDispatchProps {
    navigate?: any;
};

interface GroupListItem extends GroupInterface {
    selected?: boolean;
}

interface GroupsState {
    itemFns: ItemActionInterface[];
    filteredGroups: GroupListItem[];
    editMode: boolean;
    filterValue: string;
    groups: GroupInterface[];
    groupList: ItemInterface[];
    groupImageSrc: string | null | undefined;
    tutorialSteps: any[];
    loading: boolean;
    modal: GenericModalProps;
};

// Tutorial constants
const TutorialSteps = [
    { element: '#tutorial-create-group', popover: { title: I18n.t('tutorials.groups.create.title'), description: I18n.t('tutorials.groups.create.description') } },
    { element: '#tutorial-group-list', popover: { title: I18n.t('tutorials.groups.groupList.title'), description: I18n.t('tutorials.groups.groupList.description') } },
]

class Groups extends React.Component<GroupsProps, GroupsState> {

    constructor(props: GroupsProps) {

        super(props);

        this.state = {
            itemFns: [{
                callback: this.onDeleteGroup,
                icon: 'trash'
            }],
            filteredGroups: [],
            editMode: false,
            filterValue: '',
            groups: [],
            groupList: [],
            groupImageSrc: null,
            tutorialSteps: [],
            loading: false,
            modal: ClosedModal,
        };

    }

    componentDidMount() {

        this.setState({ tutorialSteps: TutorialSteps });

        this.getData();

    }

    dataToList = (list: any[], selectable: boolean): ItemInterface[] => {

        const returnedList: ItemInterface[] = [];
        _.each(list, item => {

            returnedList.push({
                _id: item._id,
                selectable,
                selected: !!item.selected,
                pictureUrl: item.imageUrl,
                header: item.name.toUpperCase(),
                description: item.studentsCount + ' ' + I18n.t('menu.students')
            });

        });
        return returnedList;

    };

    getData = async () => {

        this.props.openLoader();

        this.setState({ loading: true });

        const groups = await GroupModel.getGroups();
        const filteredGroups = _.orderBy(groups, 'name', 'desc');

        const groupList = this.dataToList(filteredGroups, true);

        this.setState({ filteredGroups, groups, groupList, loading: false });

        this.props.closeLoader();

    };

    onDeleteGroup = (item: ItemInterface) => {

        const { closeLoader, openLoader } = this.props;

        const deleteGroup = async (groupId: string) => {

            this.closeModal();

            openLoader();

            try {

                await GroupModel.removeGroup(groupId);
                this.getData();

            } catch (error) {

                this.setState({
                    modal: {
                        open: true,
                        title: I18n.t('buttons.actions.error'),
                        renderBody: () => <span>{I18n.t('messager.errorRecovery')}</span>,
                        renderActions: () => <OButton type='button' $color='white' $terciary onClick={this.closeModal}><span>{I18n.t('buttons.actions.understood')}</span></OButton>
                    }
                });

            }

            closeLoader();
        };

        const group = this.state.groups.find((g) => g._id === item._id);

        if (!group) return;

        if (group.studentsCount > 0) {
            return this.setState({
                modal: {
                    open: true,
                    title: I18n.t('buttons.actions.removeGroup'),
                    renderBody: () => <div>
                        <strong>{I18n.t('groups.messager.remove')}</strong>
                        <p>{I18n.t('groups.messager.removeNote')}</p>
                    </div>,
                    renderActions: () => <div>
                        <OButton type='button' $color='white' $terciary onClick={() => deleteGroup(item._id)}><span>{I18n.t('buttons.actions.remove')}</span></OButton>
                        <OButton type='button' $color='white' $terciary onClick={this.closeModal}><span>{I18n.t('buttons.actions.cancel')}</span></OButton>
                    </div>
                }
            });
        }

        deleteGroup(item._id);

    }

    onFilter = (filterValue: string) => {

        let filteredGroups = [...this.state.groups];
        let groupList = [...this.state.groupList];

        if (filterValue) {

            filteredGroups = _.filter(filteredGroups, group => group?.name?.toUpperCase().includes(filterValue.toUpperCase()))
            groupList = this.dataToList(filteredGroups, true);

        }

        this.setState({ filteredGroups, filterValue, groupList })

    }

    closeModal = () => this.setState({ modal: ClosedModal });

    onEdit = () => {

        let { groupList, filteredGroups, groups, editMode } = { ...this.state };

        editMode = !editMode;
        if (!editMode) {
            groupList = _.map(groupList, group => ({ ...group, selected: false }));
            filteredGroups = _.map(filteredGroups, group => ({ ...group, selected: false }));
            groups = _.map(groups, group => ({ ...group, selected: false }));
        }

        this.setState({ groupList, filteredGroups, groups, editMode });

    };

    onSelectGroup = (id: string, checked: boolean) => {

        let { filteredGroups, groups } = this.state;

        const newFilteredGroups = _.map(filteredGroups, group => {
            if (group._id === id) {
                return { ...group, selected: checked };
            }
            return group;
        });

        groups = _.map(groups, group => {
            if (group._id === id) {
                return { ...group, selected: checked };
            }
            return group;
        });

        const groupList = this.dataToList(newFilteredGroups, true);
        this.setState({ filteredGroups: newFilteredGroups, groups, groupList });

    }

    onSelectAll = (checked: boolean = false) => {

        let { filteredGroups, groups } = this.state;
        filteredGroups = _.map(filteredGroups, group => ({ ...group, selected: checked }));
        groups = _.map(groups, group => ({ ...group, selected: checked }));

        const groupList = this.dataToList(filteredGroups, true);
        this.setState({ filteredGroups, groups, groupList });

    }

    getAllSelected = (): GroupInterface[] => {
        const { filteredGroups } = this.state;
        const groups: GroupInterface[] = [];
        _.each(filteredGroups, group => {
            if (group.selected) {
                groups.push(group);
            }
        });
        return groups;
    }

    onActiveChallenges = async (groups: GroupInterface[]) => {
        const groupIds = _.map(groups, g => g._id);
        if (groupIds.length <= 0) return;

        try {
            this.props.openLoader();
            this.setState({ loading: true });

            await ProgressModel.forceUnlockGroupChallenges({ groupIds });

            this.setState({ loading: false });

            this.props.closeLoader();
        } catch (error) {

            this.props.closeLoader();
            this.setState({ loading: false });
            this.setState({
                modal: {
                    open: true,
                    title: I18n.t('buttons.actions.error'),
                    renderBody: () => <span>{I18n.t('messager.errorRecovery')}</span>,
                    renderActions: () => <OButton type='button' $color='white' $terciary onClick={this.closeModal}><span>{I18n.t('buttons.actions.understood')}</span></OButton>
                }
            });

        }
    };

    render() {

        const { navigate } = this.props;
        const { itemFns, filterValue, groupList, groupImageSrc, tutorialSteps, loading, modal, editMode } = this.state;

        const allSelected = _.size(groupList) > 0 && !_.find(groupList, g => !g.selected);
        const anySelected = !!_.find(groupList, g => g.selected);

        return (
            <Wrapper>
                <GenericModal
                    open={modal.open}
                    title={modal.title}
                    renderBody={modal.renderBody}
                    renderActions={modal.renderActions}
                    onClose={this.closeModal}
                />
                <PanelNav active='groups' />
                <Container style={{ paddingTop: '20px' }}>
                    <Submenu
                        allSelected={allSelected}
                        anySelected={anySelected}
                        contentEditable={true}
                        filterFn={this.onFilter}
                        filterValue={filterValue}
                        groupActions={[
                            { icon: 'unlock', label: I18n.t('students.activeChallenges'), callback: () => this.onActiveChallenges(this.getAllSelected()) }
                        ]}
                        mainAction={{ id: 'tutorial-create-group', title: I18n.t('groups.newGroup'), callback: () => navigate('/groups/new') }}
                        title={I18n.t('menu.groups')}
                        editFn={this.onEdit}
                        editMode={editMode}
                        selectAllFn={this.onSelectAll}
                    />
                    <div className='inner'>
                        <div className='p-flex'>
                            <div className='p-content'>
                                <div className='a-wrapper'>
                                    <div id='tutorial-group-list' className='a-f-wrapper'>
                                        <CustomList
                                            editMode={editMode}
                                            items={groupList}
                                            itemFns={itemFns}
                                            onClickItemFn={(item: ItemInterface) => navigate(`/groups/edit/${item._id}`)}
                                            onClickItemSpanFn={(item: ItemInterface) => this.setState({ groupImageSrc: item.pictureUrl })}
                                            onSelectItemFn={this.onSelectGroup}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </Container>
                <BottomNav
                    active='groups'
                    anySelected={anySelected}
                    editMode={editMode}
                    groupActions={[
                        { icon: 'unlock', label: I18n.t('students.activeChallenges'), callback: () => this.onActiveChallenges(this.getAllSelected()) }
                    ]}
                />
                <ImageViewer open={groupImageSrc !== null} title={I18n.t('groups.picture')} src={groupImageSrc} onClose={() => this.setState({ groupImageSrc: null })} />
                {
                    !loading && tutorialSteps && <TutorialStarter name='groups' steps={tutorialSteps} />
                }
            </Wrapper>
        );

    }

}

export default connect(null, { openLoader, closeLoader })(Groups);